import axios from 'axios'
import { checkCandidateAccess } from '../../utils/utils'

/**
 * @param {string} contestUri
 * @returns User Token is validated or not
 */
export const validateToken = async (contestUri = null, isCandidate = false) => {
  const response = axios.get(
    isCandidate
      ? `candidates/validate${contestUri ? `?contestUri=${contestUri}` : ''}`
      : 'auth/validate'
  )
  return response
}

/**
 * API for signing up the user into the system
 * @param {Object} data user singup payload
 */
export const signupUser = async (data) => {
  const response = await axios.post('auth/signup', data)
  return response
}

/**
 *API for login up the user into the system
 * @param {Object} data user login payload
 * @param {Boolean} forced Boolean value to force login
 */
export const loginUser = async (data, forced) => {
  const response = await axios.post(`auth/login?forced=${forced}`, data)
  return response
}

/**
 * API for verify user email address
 * @param {String} token  user hash token
 */
export const verifyUser = async (token, requiredToken = false) => {
  const response = await axios.post(
    `verification-token/${token}?tokenRequired=${requiredToken}`,
    null
  )
  return response
}

/**
 * API for resending verification mail to user email address
 * @param {String} token user hash token
 * @returns
 */
export const resendVerificationLink = async (token) => {
  const response = await axios.post(`verification-token/resend/${token}`, null)
  return response
}

/**
 * API for inviting user for a tenant
 * @param {Object} payload User Creation Payload {email,name,isSuperAdmin}
 * @returns
 */
export const inviteUserToTenant = async (payload) => {
  const response = await axios.post(`auth/invite-user`, payload)
  return response
}

/**
 * API for setting password for invited user
 * @param {String} token User Token
 * @param {String} hashToken User Hash Token
 * @param {String} password User Password
 * @returns
 */
export const setUserPassword = async (token, hashToken, details) => {
  const response = await axios.put(
    `auth/set-password`,
    { ...details, token: hashToken },
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return response
}

/**
 *
 * @returns Logut the user
 */
export const logoutUser = async () => {
  const response = await axios.delete(
    checkCandidateAccess() ? 'candidates/logout' : 'auth/logout'
  )
  return response
}

/**
 *
 * @param {object} options
 * @param {boolean} isCandidate
 * @returns
 */
export const sendForgotPasswordMail = async (
  options,
  isCandidate = false,
  isDeveloper = false
) => {
  const response = await axios.post(
    isCandidate
      ? 'candidates/forgot-password'
      : isDeveloper
        ? `dev/forgot-password`
        : 'auth/forgot-password',
    options
  )
  return response
}

/**
 *
 * @param {string} hash
 * @param {string} contestUri
 * @param {boolean} isCandidate
 * @returns
 */
export const forgotPasswordVerifyToken = async (
  hash,
  contestUri = null,
  isCandidate = false,
  isDeveloper = false
) => {
  const response = await axios.post(
    isCandidate
      ? `candidate-tokens/forgot-password/${hash}${
          contestUri ? `?contestUri=${contestUri}` : ''
        }`
      : isDeveloper
        ? `dev/forgot-password/verify/${hash}`
        : `verification-token/forgot-password/${hash}`
  )
  return response
}

/**
 *
 * @param {string} options
 * @param {boolean} isCandidate
 * @returns
 */
export const resetPassword = async (
  options,
  userToken,
  isCandidate = false,
  isDeveloper = false
) => {
  const response = await axios.put(
    isCandidate
      ? `candidates/reset-password`
      : isDeveloper
        ? `dev/reset-password`
        : `auth/reset-password`,
    options,
    { headers: { Authorization: `Bearer ${userToken}` } }
  )
  return response
}

/**
 *
 * @param {string} hash
 * @param {string} contestUri
 * @param {boolean} isCandidate
 * @returns
 */
export const resendForgotPasswordLink = async (
  hash,
  contestUri = null,
  isCandidate = false,
  isDeveloper = false
) => {
  const response = await axios.post(
    isCandidate
      ? `candidate-tokens/resend/invited-candidate/${hash}${
          contestUri ? `?contestUri=${contestUri}` : ''
        }`
      : isDeveloper
        ? `dev/resend/forgot-password/${hash}`
        : `verification-token/resend/forgot-password/${hash}`
  )
  return response
}

/**
 *
 * @param {string} hash
 * @param {string} contestUri
 * @param {boolean} isCandidate
 * @returns
 */
export const resendInviteLink = async (hash, contestUri, isCandidate) => {
  const response = await axios.post(
    isCandidate
      ? `candidate-tokens/resend/invited-candidate/${hash}?contestUri=${contestUri}`
      : `verification-token/resend/invited-user/${hash}`
  )
  return response
}

/**
 *
 * @param {object} payload  {previousPassword,newPassword}
 * @returns
 */
export const changePassword = async (payload) => {
  const response = await axios.put(`auth/change-password`, payload)
  return response
}

export const changePasswordForDevUser = async (payload) => {
  const response = await axios.put(`dev/change-password`, payload)
  return response
}

/**
 *
 * @param {object} payload  {email:'',password:''}
 * @returns
 */
export const loginDevUser = async (payload) => {
  const response = await axios.post(`dev/login`, payload)
  return response
}

/**
 *
 * @returns {Boolean} true if new user
 */
export const checkNewUser = async () => {
  const response = await axios.get('auth/check-new-user')
  return response
}

/**
 *
 * @param {Object} payload {tenantName,name}
 * @returns
 */
export const updateTenantDetails = async (payload) => {
  const response = await axios.put('auth/update-tenant-details', payload)
  return response
}

/**
 *
 * @param {string} candidateId
 * @param {string} contestId
 * @returns object
 */
export const generateShareableToken = async (
  contestId,
  candidateId,
  showDetails,
  showResume,
  showVideos
) => {
  const response = await axios.post(`candidates/generate-public-token`, {
    candidateId,
    contestId,
    showDetails,
    showResume,
    showVideos
  })
  return response
}

/**
 *
 * @param {string} publicId
 * @returns object
 */
export const getPublicShareToken = async (publicId) => {
  const response = await axios.get(`candidates/public-access/${publicId}/token`)
  return response
}

export const getTokenFromLinkedInCode = async (payload) => {
  const response = await axios.post(`candidates/linkedin-login`, payload)
  return response
}
